import { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, MenuProps, Row } from 'antd';
import { CheckOutlined, SortDescendingOutlined } from '@ant-design/icons';

import { useSearchParams } from '../../../../hooks/useSearchParams';
import { getValidSearchParams } from '../../../../utils';
import { Order } from '../../../../store/types';

export type CommentOrderColumn =
  | 'id'
  | 'text'
  | 'numberOfLikes'
  | 'numberOfDislikes'
  | 'numberOfComments'
  | 'createdAt'
  | 'updatedAt';

interface QueryValues {
  comment_orderBy: Order;
  comment_orderByColumn: CommentOrderColumn;
}

const createKey = (
  { comment_orderByColumn,
    comment_orderBy }: QueryValues,
): string => `${comment_orderByColumn}_${comment_orderBy}`;
const DEFAULT_ORDER_BY_COLUMN: CommentOrderColumn = 'createdAt';
const DEFAULT_ORDER_BY: Order = 'DESC';

const DEFAULT_KEY = createKey({ comment_orderByColumn: DEFAULT_ORDER_BY_COLUMN, comment_orderBy: DEFAULT_ORDER_BY });

const menuData: { label: string; comment_orderByColumn: CommentOrderColumn; comment_orderBy: Order }[] = [
  { label: 'Most liked', comment_orderByColumn: 'numberOfLikes', comment_orderBy: 'DESC' },
  { label: 'Less liked', comment_orderByColumn: 'numberOfLikes', comment_orderBy: 'ASC' },
  { label: 'Most disliked', comment_orderByColumn: 'numberOfDislikes', comment_orderBy: 'DESC' },
  { label: 'Less disliked', comment_orderByColumn: 'numberOfDislikes', comment_orderBy: 'ASC' },
  { label: 'Newest', comment_orderByColumn: 'createdAt', comment_orderBy: 'DESC' },
  { label: 'Oldest', comment_orderByColumn: 'createdAt', comment_orderBy: 'ASC' },
  { label: 'Most commented', comment_orderByColumn: 'numberOfComments', comment_orderBy: 'DESC' },
];

function SortBy() {
  const [selectedKey, setSelectedKey] = useState<string>(DEFAULT_KEY);
  const { searchParams, replaceSearchParams } = useSearchParams();

  useEffect(() => {
    const { comment_orderBy, comment_orderByColumn } = getValidSearchParams<QueryValues>('*', searchParams);

    setSelectedKey(comment_orderBy && comment_orderByColumn ? createKey({
      comment_orderBy,
      comment_orderByColumn,
    }) : DEFAULT_KEY);
  }, [searchParams]);

  const handleMenuClick = (comment_orderByColumn: CommentOrderColumn, comment_orderBy: Order) => {
    const key = createKey({ comment_orderByColumn, comment_orderBy });

    setSelectedKey(key);
    replaceSearchParams({ comment_orderByColumn, comment_orderBy });
  };

  const sortMenu: MenuProps = useMemo(
    () => ({
      style: { width: 200 },
      items: [
        ...menuData.map(({ label, comment_orderByColumn, comment_orderBy }) => {
          const key = createKey({ comment_orderByColumn, comment_orderBy });

          return {
            key,
            label: (
              <Row justify="space-between" align="middle" gutter={16}>
                {label}
                {' '}
                {selectedKey === key ? <CheckOutlined style={{ margin: '0 8px 0 16px' }} /> : null}
              </Row>
            ),
            onClick: () => handleMenuClick(comment_orderByColumn, comment_orderBy),
          };
        }),
      ],
    }),
    [selectedKey],
  );

  return (
    <Dropdown menu={sortMenu} trigger={['click']} placement="bottomRight">
      <Button icon={<SortDescendingOutlined />}>Sort</Button>
    </Dropdown>
  );
}

export default SortBy;
